import React, { useState } from 'react';
import { toast } from 'react-toastify';
import '../../components/orbit-deck/deck.css';
import { DeckContext } from '../../components/orbit-deck/DeckContext';
import type { DeckContextItem, DeckContextState } from '../../components/orbit-deck/types';
import { readDocument } from '../orbit-deck/deckModel';
import { ingest } from '../../orbit/lib/intakeApi';
import { hostOf } from '../../lib/site';

/*
 * Add context, beside the Journey.
 *
 * The same panel the deck carries, and deliberately the same one: handing Orbit
 * a document instead of answering a question is one idea, and it had grown two
 * shapes, a card on the deck and a drawer everywhere else. A person who learns
 * it in one place should recognise it in the other, so this is the deck's
 * component with the Journey's work behind it rather than a second version of
 * it that would drift.
 *
 * Whatever lands here is read into the company dossier, which is what the
 * phases are made of, so the Journey re-reads afterwards and the questions it
 * answered leave on their own.
 */

export const JourneyContext: React.FC<{
  /** The company the Room is open on. Without it the server reads into the
   *  caller's own company, which for a partner is not the client on screen. */
  clientId: string;
  /** Re-read the Room once Orbit has taken something in. */
  onAdded: () => void;
}> = ({ clientId, onAdded }) => {
  // What this visit has handed over. Session-scoped on purpose: it is a
  // receipt for what you just did, not the company's document record, which
  // lives under Documents and is a different question.
  const [added, setAdded] = useState<DeckContextItem[]>([]);

  const absorb = async (label: string, kind: string, work: () => Promise<{ facts: number; queued: boolean }>) => {
    let read: { facts: number; queued: boolean };
    try {
      read = await work();
    } catch {
      toast.error(`Could not read ${label}.`);
      // Rejecting is what keeps the panel's text on screen, so nothing has to
      // be typed twice.
      throw new Error('context failed');
    }
    setAdded((a) => [...a, { key: `${kind}:${a.length}:${label}`, label, kind }]);
    if (read.queued) toast.success(`${label} is being read now.`);
    else toast.success(`${label} read. ${read.facts} ${read.facts === 1 ? 'fact' : 'facts'} updated.`);
    onAdded();
  };

  const ctx: DeckContextState = {
    items: added,
    onFile: (file) => absorb(file.name, 'document', async () => {
      const res = await readDocument(file, clientId || null);
      return { facts: res.written, queued: false };
    }),
    onNote: (text) => absorb('Pasted notes', 'note', async () => {
      const res = await ingest('paste', text, clientId || undefined);
      return { facts: (res.deltas || []).length, queued: res.mode === 'async' };
    }),
    onLink: (link) => absorb(hostOf(link) || link, 'link', async () => {
      const res = await ingest('url', link, clientId || undefined);
      return { facts: (res.deltas || []).length, queued: res.mode === 'async' };
    }),
  };

  return <DeckContext ctx={ctx} />;
};

export default JourneyContext;
